import { Alert, Box, Button, Chip, Divider, Typography } from "@mui/material";
import ContentShell from "components/ContentShell";
import FilePicker from "components/FilePicker";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import ImagePreviewBox from "components/ImagePreviewBox";
import TranslationButtons from "components/TranslationButtons";
import BulkTranslationUpload from "components/BulkTranslationUpload";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import useStyles from "styles/banner/bannerEditor";
import configData from "config/config.json";
import {
  createChallengeRewardImage,
  getChallengeRewardImageInfo,
  getChallengeRewardImages,
  updateChallengeRewardImage,
} from "services/challengeRewardImageService";
import {
  uploadContentTranslations,
  uploadImageV2,
} from "services/uploadService";
import { DEFAULT_IMAGE_DIMENSIONS, ImageType } from "utils/imageConstants";
import { REWARD_TYPES, getRewardTypeMeta } from "./rewardTypeConfig";

const TRANSLATION_ASSET_TYPE = "CHALLENGE_REWARD_DESCRIPTION";
const REWARD_DIMS = DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE];

const SHOW_BULK_TRANSLATION_UPLOAD = false;

/** Count how many unique {{textN}} placeholders appear in a string. */
const countPlaceholders = (text: string): number => {
  if (!text) return 0;
  const matches = text.match(/\{\{text\d+\}\}/g);
  return matches ? new Set(matches).size : 0;
};

/** The sorted set of placeholder numbers used in a string. */
const getPlaceholderNumbers = (text: string): number[] => {
  if (!text) return [];
  const matches = text.match(/\{\{text(\d+)\}\}/g);
  if (!matches) return [];
  return [...new Set(matches)]
    .map((m) => parseInt(m.replace(/\{\{text|\}\}/g, ""), 10))
    .sort((a, b) => a - b);
};

interface RewardImageDetails {
  rewardType: string;
  descriptionTemplate: string;
  descriptionTemplateTrans: Record<string, string>;
}

const _initialDetails: RewardImageDetails = {
  rewardType: "",
  descriptionTemplate: "",
  descriptionTemplateTrans: {},
};

const ChallengeRewardImageEditor = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<RewardImageDetails>({
    ..._initialDetails,
  });
  const [usedRewardTypes, setUsedRewardTypes] = useState<string[]>([]);
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDims, setImageDims] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>();

  const { isOpen, close, open } = useOpenClose();
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null as string | null,
    viewData: [] as any[],
    modal: "" as string,
  });

  const selectedMeta = getRewardTypeMeta(details.rewardType);
  const requiredPlaceholders = selectedMeta?.dynamicValues ?? 0;

  const cancel = () => navigate(configData.routes.challenges.rewardImages);

  const rewardTypeOptions = useMemo(() => {
    return REWARD_TYPES.filter((rt) => {
      if (isEdit && rt.id === details.rewardType) return true;
      return !usedRewardTypes.includes(rt.id);
    }).map((rt) => ({ id: rt.id, label: rt.label }));
  }, [details.rewardType, isEdit, usedRewardTypes]);

  // Live object-URL preview of the picked image.
  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const fetchUsedRewardTypes = async () => {
    try {
      const resp = await getChallengeRewardImages();
      const used = (resp?.data || [])
        .map((item: any) => item?.rewardType)
        .filter(Boolean);
      setUsedRewardTypes(used);
    } catch (error: any) {
      showSnackbar("Failed to load existing reward images", "error");
    }
  };

  const fetchInfo = async () => {
    setLoading(true);
    try {
      const resp = await getChallengeRewardImageInfo(id);
      if (resp?.data) {
        const data = resp.data;
        const descriptionTemplateTrans = {
          ...(data.descriptionTemplateTrans || {}),
        };
        if (data.descriptionTemplate && !descriptionTemplateTrans["en"]) {
          descriptionTemplateTrans["en"] = data.descriptionTemplate;
        }
        setDetails({
          rewardType: data.rewardType || "",
          descriptionTemplate: data.descriptionTemplate || "",
          descriptionTemplateTrans,
        });
        setCurrentImageUrl(data.imageUrl || undefined);
      }
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsedRewardTypes();
    if (isEdit) fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Validate the placeholder count/numbering of one template string against the reward type. */
  const validatePlaceholders = (text: string): string | null => {
    const count = countPlaceholders(text);
    if (count !== requiredPlaceholders) {
      if (requiredPlaceholders === 0) {
        return `"${selectedMeta?.label}" takes no dynamic values. Remove the {{text...}} placeholders.`;
      }
      const expected = Array.from(
        { length: requiredPlaceholders },
        (_, i) => `{{text${i + 1}}}`,
      ).join(", ");
      return `"${selectedMeta?.label}" requires exactly ${requiredPlaceholders} placeholder(s): ${expected}. Found ${count}.`;
    }
    // Sequential numbering starting at 1.
    const numbers = getPlaceholderNumbers(text);
    for (let i = 0; i < numbers.length; i++) {
      if (numbers[i] !== i + 1) {
        return `Placeholders must be numbered sequentially from {{text1}}. Found a gap at position ${i + 1}.`;
      }
    }
    return null;
  };

  const validateAllTranslations = (): string | null => {
    for (const [lang, text] of Object.entries(
      details.descriptionTemplateTrans || {},
    )) {
      const err = validatePlaceholders(text);
      if (err) return `Translation [${lang}]: ${err}`;
    }
    return null;
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async () => {
    try {
      if (!details.rewardType) {
        showSnackbar("Reward Type is required", "error");
        return;
      }
      if (!details.descriptionTemplate) {
        showSnackbar("Description is required", "error");
        return;
      }

      const templateErr = validatePlaceholders(details.descriptionTemplate);
      if (templateErr) {
        showSnackbar(templateErr, "error");
        return;
      }
      const transErr = validateAllTranslations();
      if (transErr) {
        showSnackbar(transErr, "error");
        return;
      }

      setLoading(true);

      const basePayload: any = {
        descriptionTemplate: details.descriptionTemplate,
        descriptionTemplateTrans: details.descriptionTemplateTrans,
        ...(imageFile && { fileType: "image/webp" }),
      };

      let uploadImageUrl: string | undefined;

      if (isEdit) {
        const resp = await updateChallengeRewardImage(basePayload, id);
        uploadImageUrl = resp?.data?.uploadImageUrl;
      } else {
        const resp = await createChallengeRewardImage({
          ...basePayload,
          rewardType: details.rewardType,
        });
        uploadImageUrl = resp?.data?.uploadImageUrl;
        if (translationFile && resp?.data?._id) {
          await uploadContentTranslations(
            translationFile,
            TRANSLATION_ASSET_TYPE,
            resp.data._id,
          );
        }
      }

      // Push the actual image bytes to the presigned URL, if an image was picked.
      if (imageFile && uploadImageUrl) {
        const uploadResp = await uploadImageV2({
          data: imageFile,
          uploadUrl: uploadImageUrl,
          resize: true,
          resizeToWidth: imageDims?.targetWidth || REWARD_DIMS.width,
          resizeToHeight: imageDims?.targetHeight || REWARD_DIMS.height,
        });
        if (!uploadResp?.status) {
          throw new Error(uploadResp?.message || "Image upload failed.");
        }
      }

      showSnackbar(
        isEdit
          ? "Reward image updated successfully"
          : "Reward image created successfully",
      );
      setLoading(false);
      cancel();
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  // ---- Manual per-language translation modal (mirrors the Challenge Titles editor) ----
  const getTranslatedData = () =>
    Object.entries(details.descriptionTemplateTrans || {}).map(
      ([key, value]) => ({
        lang: key,
        inputValue: value,
      }),
    );

  const handleOpenModal = (type: string, field: string) => {
    setModalData((state) =>
      type === "view"
        ? { ...state, viewData: getTranslatedData(), field, modal: type }
        : { ...state, field, modal: type, language: "", inputValue: "" },
    );
    open();
  };

  const handleModalChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete the English translation (default language)",
        "error",
      );
      return;
    }
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));
    setDetails((prev) => {
      const updated = { ...prev.descriptionTemplateTrans };
      delete updated[data.lang];
      return { ...prev, descriptionTemplateTrans: updated };
    });
  };

  const handleAddTranslation = () => {
    if (!modalData.language || !modalData.inputValue) {
      showSnackbar("Language and translation are required", "error");
      return;
    }
    const err = validatePlaceholders(modalData.inputValue);
    if (err) {
      showSnackbar(err, "error");
      return;
    }
    setDetails((prev) => {
      const updated = {
        ...prev.descriptionTemplateTrans,
        [modalData.language]: modalData.inputValue,
      };
      const newDetails = { ...prev, descriptionTemplateTrans: updated };
      if (modalData.language === "en")
        newDetails.descriptionTemplate = modalData.inputValue;
      return newDetails;
    });
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const translationCount = Object.keys(
    details.descriptionTemplateTrans || {},
  ).length;

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={isEdit ? "Edit Reward Image" : "Create Reward Image"}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="button"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="button" variant="contained" onClick={submit}>
            {isEdit ? "Update" : "Save"}
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Reward Type"
              name="rewardType"
              value={details.rewardType}
              options={rewardTypeOptions}
              onChange={handleChange}
              disabled={isEdit}
            />
          </Box>

          <Box sx={{ minWidth: 320, flex: 1 }}>
            <SimpleTextInput
              label="Description (English)"
              name="descriptionTemplate"
              value={details.descriptionTemplate}
              onChange={(e: any) => {
                handleChange(e);
                setDetails((prev) => ({
                  ...prev,
                  descriptionTemplateTrans: {
                    ...prev.descriptionTemplateTrans,
                    en: e.target.value,
                  },
                }));
              }}
              button={
                <TranslationButtons
                  field="descriptionTemplateTrans"
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
        </Box>

        {selectedMeta && (
          <Alert
            severity={
              validatePlaceholders(details.descriptionTemplate)
                ? "warning"
                : "info"
            }
            sx={{ mt: 2 }}
          >
            {selectedMeta.hint}
            {validatePlaceholders(details.descriptionTemplate) && (
              <>
                <br />
                <strong>Current issue:</strong>{" "}
                {validatePlaceholders(details.descriptionTemplate)}
              </>
            )}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Active translations
          </Typography>
          <Chip
            size="small"
            label={`${translationCount} language${translationCount === 1 ? "" : "s"}`}
            color={translationCount > 0 ? "primary" : "default"}
          />
          {details.descriptionTemplateTrans?.en && (
            <Chip size="small" label="English ready" color="success" />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Reward image */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Reward Image
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ width: 200 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
            >
              {previewUrl ? "New image (preview)" : "Current image"}
            </Typography>
            <ImagePreviewBox
              url={previewUrl || currentImageUrl}
              alt={details.rewardType}
              height={200}
              emptyText="No image yet"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <FilePicker
              label="Choose image"
              name="reward-image"
              fileType="image/*"
              showPreview={false}
              imageType={ImageType.CHALLENGE_REWARD_IMAGE}
              enableDimensionControl={true}
              metaData={{
                note: `Recommended: ${REWARD_DIMS.width}×${REWARD_DIMS.height}px. Resized to WebP, aspect ratio preserved.`,
              }}
              onFileSelect={(f: any, d?: any) => {
                setImageFile(f || null);
                setImageDims(d ?? null);
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Optional. Used in-game for this reward type when a challenge has
              no custom image.
            </Typography>
          </Box>
        </Box>

        {/* Bulk translation upload — one reward type's description translations at a time */}
        {SHOW_BULK_TRANSLATION_UPLOAD && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                "& .MuiCard-root": {
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { boxShadow: "none" },
                },
              }}
            >
              {isEdit ? (
                <BulkTranslationUpload
                  assetType={TRANSLATION_ASSET_TYPE}
                  assetId={id!}
                  fields={[
                    {
                      label: "Description",
                      fieldName: "descriptionTemplateTrans",
                      dummyText: "You will win {{text1}} coins",
                    },
                  ]}
                  onSuccess={fetchInfo}
                />
              ) : (
                <BulkTranslationUpload
                  assetType={TRANSLATION_ASSET_TYPE}
                  fields={[
                    {
                      label: "Description",
                      fieldName: "descriptionTemplateTrans",
                      dummyText: "You will win {{text1}} coins",
                    },
                  ]}
                  deferUpload
                  onFileChange={setTranslationFile}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>

      <ViewLanguageModal
        isMultiline={false}
        label="Add Description Translation"
        isOpen={isOpen}
        close={close}
        onChange={handleModalChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick,
          handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignBody: "center",
            alignHead: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default ChallengeRewardImageEditor;
